// Copyright (c) 2016 Deere & Company

import Attachment, {getDefaultState as getAttachmentDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-attachment';
import Battery, {getDefaultState as getBatteryDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-battery';
import IncentiveClaim, {getDefaultState as getIncentiveClaimDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-incentive';
import JdPromise, {getDefaultState as getJdpDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-jd-promise';
import MaintenancePlan, {getDefaultState as getMaintenancePlanDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-maintenance-plan';
import MassChannelPdi, {getDefaultState as getPdiDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-mass-channel-pdi';
import Normal, {getDefaultState as getNormalDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-normal';
import Part, {getDefaultState as getPartsDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-part';
import Pip, {getDefaultState as getPipDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-pip';
import SpecialAllowance, {getDefaultState as getSpaDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-special-allowance';
import Track, {getDefaultState as getTrackDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-track';
import WearPart, {getDefaultState as getWearPartDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-wear-part';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import KeyValuePairCollection from '@deere/cps-it.warranty-model-factory/lib/key-value-pair-collection';
import {getDefaultState as getDefaultDealerState} from '@deere/cps-it.warranty-model-factory/lib/dealer';
import {getDefaultState as getDefaultKeyValuePairState} from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import {getDefaultState as getOpenMaintenancePlanDefaultState} from '@deere/cps-it.warranty-model-factory/lib/open-maintenance-plan';
import {getDefaultState as getSelectedProgramDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-incentive-program';
import pick from 'lodash.pick';

const getActionsByMode = (mode, allowedActions) =>
    allowedActions.filter(action => mode === action.mode);

export const isNotEmpty = value =>
    !(/^\s*$/.test(value)) && !(value === null);

export const validateClaimUseAmounts = (value, claim) =>
    claim.useAmounts.every(item => isNotEmpty(item.amount));

export const validateClaimUseAmountUnits = (value, claim) =>
    claim.useAmounts.every(item => isNotEmpty(item.unit.key));

export const isAfterStartDate = (endDate, startDate) =>
    (startDate && endDate) ?
        startDate <= endDate :
        true;

export const shouldRunAdditionalAction = action =>
    action.id !== 'REVISE';

export const doesPropertyExist = (property, value, list) =>
    list.some(item => item[property] === value);

export const doesAccountHaveMatch = (accountId, accountList) => {

    let matchingDealer;

    if (accountId) {

        matchingDealer = accountList.find(option => option.account === accountId);

    }

    return matchingDealer || false;

};

// for file upload panel with claim number

export const getServiceConfig = claimNumber => ({
    className: 'BUS2222',
    classType: 'BO',
    objectKey: claimNumber
});

// for file upload panel where temporaryObject Key is taken if claim number does not exist

export const getTempAttachmentServiceConfig = (claimNumber, temporaryObjectKey) => ({
    className: 'BUS2222',
    classType: claimNumber ? 'BO' : 'OT',
    objectKey: claimNumber ? claimNumber : temporaryObjectKey
});

export const findMatchingAccount = (accountId, accountList) =>
    doesAccountHaveMatch(accountId, accountList) || getDefaultDealerState();

export const findMatchingKey = (key, list) => {

    let matchedObject;

    if (key) {

        matchedObject = list.find(option => option.key === key);

    }

    return matchedObject ?
        matchedObject :
        getDefaultKeyValuePairState();

};

export const findMatchingSelectedProgram = (programName, list) => {

    let matchedObject;

    if (programName) {

        matchedObject = list.find(option => option.programName === programName);

    }

    return matchedObject ?
        matchedObject :
        getSelectedProgramDefaultState();

};

export const findMatchingPlan = (planName, plans) => {

    let matchedObject;

    if (planName) {

        matchedObject = plans.find(plan => plan.planName === planName);

    }

    return matchedObject ?
        matchedObject :
        getOpenMaintenancePlanDefaultState();

};

export const getAllowedActions = (isEditable, allowedActions) =>
    isEditable ?
        getActionsByMode('CHG', allowedActions) :
        getActionsByMode('DIS', allowedActions);

export const getRepairedDateErrorMessage = repairDate =>
    repairDate === null ?
        'REPAIRED_DATE_IS_REQUIRED' :
        'REPAIRED_DATE_CANNOT_BE_BEFORE_FAILED_DATE';

export const validateUseAmountIfPinExists = (claim, allowedUnitsLength) =>
    allowedUnitsLength ?
        isNotEmpty(claim.useAmount.unit.key) && isNotEmpty(claim.useAmount.amount) :
        true;

export const validateUseAmountsIfPinExists = (claim, allowedUnitsLength) =>
    allowedUnitsLength ?
        validateClaimUseAmountUnits('', claim) && validateClaimUseAmounts('', claim) :
        true;

export const isNotNull = value =>
    value !== null;

export const isNumberInputValid = value =>
    typeof value === 'number';

export const isReadOnlyAction = action =>
    ['DRAFT', 'SAVE_VALD', 'FRWRD', 'SUB', 'DEL', 'DIS_VSR', 'RECONCILE', 'UNRECONCIL', 'RET', 'PUSH_XML'].some(actionName => actionName === action.id);

export const isClaimEditable = (validationStatus, selectedAction) => {

    let returnValue;

    if (selectedAction.id === 'UNSUB') {

        returnValue = !validationStatus;

    } else {

        returnValue = validationStatus ?
            true :
            !isReadOnlyAction(selectedAction);

    }

    return returnValue;

};

export const isRepairDateAfterFailureDate = (repairDate, failureDate) =>
    isNumberInputValid(repairDate) ?
        failureDate <= repairDate :
        true;

export const isActionResetFormValidation = action =>
    ['DRAFT', 'SUB'].some(actionName => actionName === action);

export const validAmountOfUse = amountOfUse => {

    const amount = parseFloat(amountOfUse);

    const stripLeadingZeros = stringToStrip =>
        stringToStrip.length > 1 ?
            stringToStrip.replace(/^0+/, '') :
            stringToStrip;

    return isNaN(amount) || amount < 0 ?
        '' :
        stripLeadingZeros(amountOfUse);

};

export const getClaimModel = claimType => {

    const map = {
        JDP: JdPromise,
        MTC: MaintenancePlan,
        PDI: MassChannelPdi,
        PDP: MassChannelPdi,
        PMI: IncentiveClaim,
        WRP: WearPart,
        ZNRM: Normal,
        ZPAR: Part,
        ZPIP: Pip,
        ZSPA: SpecialAllowance,
        ZZBT: Battery,
        ZZNS: Attachment,
        ZZTK: Track
    };

    return map[claimType];

};

export const actionMatches = (action, actionId) => {

    let returnValue;

    if (typeof action === 'undefined') {

        returnValue = false;

    } else {

        returnValue = actionId === action.id;

    }

    return returnValue;

};

export const filterOtherCreditLineItems = (otherCredits, itemKey = '08') => otherCredits.filter(item => item.details.key !== itemKey);

export const hasEnoughDataToDetermineApplyTravel = claim =>
    claim.responsibleAccount.account.length && claim.pin.length && claim.failureInfo.failurePartNumber.length && claim.failureInfo.failureDate && claim.useAmounts.length && claim.useAmounts.every(o => o.amount !== '');

/* eslint-disable complexity */

export const showApplyTravelShouldUpdate = (oldClaim, newClaim) =>
    oldClaim.responsibleAccount.account !== newClaim.responsibleAccount.account ||
    oldClaim.pin !== newClaim.pin ||
    oldClaim.failureInfo.failurePartNumber !== newClaim.failureInfo.failurePartNumber ||
    oldClaim.failureInfo.failureDate !== newClaim.failureInfo.failureDate ||
    (oldClaim.useAmounts.length === newClaim.useAmounts.length ? oldClaim.useAmounts.some((obj, index) => obj.amount !== newClaim.useAmounts[index].amount) : false);

export const mergeClaimTypes = (fromClaim, toClaim) => {

    const propsToBeOverridden = pick(toClaim, [
        'goodsInstallationVat',
        'hideClaimValueDetails',
        'showAmountsInLocalCurrency',
        'claimValueDetails',
        'claimStatus',
        'claimNumber',
        'claimType',
        'errors',
        'recordInfo',
        'reimbursementDetails',
        'subClaimType',
        'selectedAction',
        'validationFailed'
    ]);

    // adjust default allowed actions based on the claim type

    const allowedActions = toClaim.claimType.key === 'ZZMK' ?
        fromClaim.allowedActions.filter(action => action.id !== 'DRAFT') :
        fromClaim.allowedActions;

    if (toClaim.claimType.key === 'ZZMK') {

        const saveValidateAction = new ClaimAction({
            actionName: 'SAVE_VALD',
            id: 'SAVE_VALD',
            mode: 'CHG'
        });

        if (!allowedActions.some(action => action.id === 'SAVE_VALD')) {

            allowedActions.push(saveValidateAction);

        }

    }

    // remove any loaner hours other credits from special allowance claims

    const otherCredits = fromClaim.claimType.key === 'ZSPA' ? filterOtherCreditLineItems(fromClaim.otherCredits, '11') : fromClaim.otherCredits;

    const normalizedFromClaim = {
        ...fromClaim,
        allowedActions,
        otherCredits
    };

    if (normalizedFromClaim.claimType.key === 'ZZBT') {

        const downtime = getDefaultKeyValuePairState();

        Reflect.defineProperty(normalizedFromClaim.additionalFailureInfo, 'downtime', {
            value: downtime
        });

    }

    const mergedClaim = {
        ...toClaim,
        ...normalizedFromClaim,
        ...propsToBeOverridden
    };

    const key = mergedClaim.claimType.key === 'ZZMK' ?
        mergedClaim.subClaimType.key :
        mergedClaim.claimType.key;

    const Model = getClaimModel(key);

    return new Model(pick(mergedClaim, Object.keys(toClaim)));

};

/* eslint-enable complexity */

export const getClaimAction = claimKey => {

    const map = {
        JDP: 'JDP',
        MCC: 'MCC',
        MTC: 'MAINTENANCE_PLAN',
        PDI: 'MASS_CHANNEL_PDI',
        PDP: 'MASS_CHANNEL_PDI',
        PMI: 'INCENTIVE',
        WRP: 'WEAR_PARTS',
        ZNRM: 'NORMAL',
        ZPAR: 'PARTS',
        ZPIP: 'PIP',
        ZSPA: 'SPA',
        ZZBT: 'BATTERY',
        ZZNS: 'ATTACHMENT',
        ZZTK: 'TRACK'
    };

    return map[claimKey];

};

export const getClaimDefaultState = claimKey => {

    const map = {
        JDP: getJdpDefaultState,
        MTC: getMaintenancePlanDefaultState,
        PDI: getPdiDefaultState,
        PDP: getPdiDefaultState,
        PMI: getIncentiveClaimDefaultState,
        WRP: getWearPartDefaultState,
        ZNRM: getNormalDefaultState,
        ZPAR: getPartsDefaultState,
        ZPIP: getPipDefaultState,
        ZSPA: getSpaDefaultState,
        ZZBT: getBatteryDefaultState,
        ZZNS: getAttachmentDefaultState,
        ZZTK: getTrackDefaultState
    };

    return map[claimKey]();

};

export const shouldUpdateUseAmount = (newUseAmount, currentUseAmount, allowedUnits) =>
    doesPropertyExist('key', newUseAmount, allowedUnits) && !currentUseAmount;

export const getPartUnit = (currentUnit, newUnit) =>
    currentUnit.key ?
        currentUnit :
        newUnit;

export const getFieldClassName = (fieldClass, isValid, highlightClass) => {

    const fieldClassName = `${fieldClass} ${highlightClass}`;

    return isValid ?
        fieldClassName :
        `${fieldClassName} claim-error`;

};

const getHighlightClass = (type, className) => {

    const map = {
        E: `${className}-error`,
        W: `${className}-warning`
    };

    return map[type];

};

export const hasVsrErrorForClaimField = (errors, fieldName, isEditable) => {

    const errorItem = errors.find(error => error.errorField.key === fieldName);

    return errorItem && isEditable ?
        getHighlightClass(errorItem.type.key, 'highlight') :
        '';

};

export const getVsrMessage = (errors, fieldName, isEditable) => {

    const errorItem = errors.find(error => error.errorField.key === fieldName);

    return errorItem && isEditable ?
        errorItem.message :
        '';

};

export const getVsrMessageForClaimItem = (errors, itemType, itemNumber) => {

    const errorItem = errors.find(error => error.errorField.itemType === itemType && parseInt(error.errorField.itemNumber, 10) === itemNumber);

    return errorItem ?
        errorItem.message :
        '';

};

export const hasVsrErrorForClaimItem = (errors, itemType, itemNumber) => {

    const errorItem = errors.find(error => error.errorField.itemType === itemType && parseInt(error.errorField.itemNumber, 10) === itemNumber);

    return errorItem ?
        getHighlightClass(errorItem.type.key, 'highlight-item') :
        '';

};

export const getOtherCreditTypes = (otherCredits, selectedCredit) => {

    const {key, value} = selectedCredit;

    if (key === '' && value === '') {

        return otherCredits;

    }

    return otherCredits.some(credit => credit.key === selectedCredit.key) ?
    otherCredits :
    KeyValuePairCollection.update(otherCredits, {
        $push: [selectedCredit]
    });

};

export const isResponsibleAccountEditable = (isEditable, claimStatus) =>
    isEditable && claimStatus.key === '' || isEditable && claimStatus.key === 'ZDFT';

export const scrollAccordionIntoView = className => {

    const element = document.getElementsByClassName(className)[0];

    element.scrollIntoView();

};

export const sortActions = actions => actions.sort((a, b) => {

    const actionA = a.id.toLowerCase();
    const actionB = b.id.toLowerCase();

    if (actionA < actionB) {

        return -1;

    }

    if (actionA > actionB) {

        return 1;

    }

    return 0;

});

const isActionAvailable = (actions, actionId) =>
    actions.some(action => action.id === actionId);

const findAction = (actions, actionId) =>
    actions.find(action => action.id === actionId);

/* eslint-disable complexity */

export const getPrimaryAction = actions => {

    if (isActionAvailable(actions, 'RECONCILE')) {

        return findAction(actions, 'RECONCILE');

    } else if (isActionAvailable(actions, 'UNRECONCIL')) {

        return findAction(actions, 'UNRECONCIL');

    } else if (isActionAvailable(actions, 'UNSUB')) {

        return findAction(actions, 'UNSUB');

    } else if (isActionAvailable(actions, 'SUB')) {

        return findAction(actions, 'SUB');

    } else if (isActionAvailable(actions, 'FRWRD')) {

        return findAction(actions, 'FRWRD');

    } else if (isActionAvailable(actions, 'REVISE')) {

        return findAction(actions, 'REVISE');

    } else if (isActionAvailable(actions, 'SAVE_VALD')) {

        return findAction(actions, 'SAVE_VALD');

    } else if (isActionAvailable(actions, 'DRAFT')) {

        return findAction(actions, 'DRAFT');

    }

    return null;

};

/* eslint-enable complexity */

export const useAmountIndexToChange = (complianceApplies, index) =>
    complianceApplies ?
        index :
        0;

export const isValidateButtonShown = (isEditable, showValidate) =>
    isEditable && showValidate;



// WEBPACK FOOTER //
// ./src/claim/shared/services/claim-shared-helper.js